<script lang="ts">
import type { AvatarProps, ChipProps, KbdProps } from '@nuxt/ui';
import type { Editor, Range } from '@tiptap/core';
import type { UseFuseOptions } from '@vueuse/integrations/useFuse';
import type { FuseResult } from 'fuse.js';
import type { PopoverContentEmits, PopoverContentProps } from 'reka-ui';
import type { EmitsToProps } from 'vue';
import type { SuggestionMenuContentSlots, SuggestionMenuContentUiSlots } from './SuggestionMenuContent.vue';
import { computed, ref, shallowRef, toRef, watch } from 'vue';

export interface SuggestionItem {
	as?: any;

	prefix?: string;
	label?: string;
	suffix?: string;
	/**
	 * @IconifyIcon
	 */
	icon?: string;
	avatar?: AvatarProps;
	chip?: ChipProps;
	kbds?: KbdProps['value'][] | KbdProps[];
	active?: boolean;
	loading?: boolean;
	disabled?: boolean;
	slot?: string;
	/**
	 * The placeholder to display when the item has children.
	 */
	placeholder?: string;

	onSelect: (props: {
		editor: Editor;
		range: Range;
	}) => void;

	class?: any;
	ui?: Omit<SuggestionMenuContentUiSlots, 'content' | 'viewport' | 'group' | 'label'>;
	[key: string]: any;
}

export interface SuggestionGroup<T extends SuggestionItem = SuggestionItem> {
	id: string;
	label?: string;
	slot?: string;
	items?: T[];
	/**
	 * Whether to filter group items with [useFuse](https://vueuse.org/integrations/useFuse).
	 * When `true`, items will not be filtered which is useful for custom filtering (useAsyncData, useFetch, etc.).
	 * @defaultValue false
	 */
	ignoreFilter?: boolean;
	/** Filter group items after the search happened. */
	postFilter?: (searchTerm: string, items: T[]) => T[];
	/**
	 * The icon displayed when an item is highlighted.
	 * @IconifyIcon
	 */
	highlightedIcon?: string;
}

export interface SuggestionMenuProps<G extends SuggestionGroup<T> = SuggestionGroup<any>, T extends SuggestionItem = SuggestionItem> {
	editor?: Editor | null;
	char?: string;
	groups?: G[];
	searchItems?: (query: string, items: (T & { group: string })[]) => (T & { group: string })[];
	selector?: string;
	pluginKey?: string | PluginKey;
	allowSpaces?: boolean;
	allowLeadingSpace?: boolean;
	allowToIncludeChar?: boolean;
	allowedPrefixes?: string[] | null;
	startOfLine?: boolean;
	decorationTag?: string;
	decorationClass?: string;
	decorationContent?: string;

	// TODO remove?
	maxHeight?: number;

	/**
	 * The key used to get the label from the item.
	 * @defaultValue 'label'
	 */
	labelKey?: string;

	content?: Omit<PopoverContentProps, 'as' | 'asChild' | 'forceMount'> & Partial<EmitsToProps<PopoverContentEmits>>;
	fuse?: UseFuseOptions<T>;
}
</script>

<script setup lang="ts" generic="G extends SuggestionGroup<T>, T extends SuggestionItem">
import type { ClientRectObject } from '@floating-ui/vue';
import { findSuggestionMatch as defaultFindSuggestionMatch, Suggestion } from '@tiptap/suggestion';
import { useFuse } from '@vueuse/integrations/useFuse';
import { defu } from 'defu';
import { PluginKey } from 'prosemirror-state';
import { PopoverContent, PopoverPortal, PopoverRoot } from 'reka-ui';
import { omit } from '#layers/base/public';
import SuggestionMenuContent from './SuggestionMenuContent.vue';

const {
	char = '@',
	allowSpaces = false,
	allowLeadingSpace = true,
	allowToIncludeChar = false,
	allowedPrefixes = [' '],
	startOfLine = false,
	decorationTag = 'span',
	decorationClass = 'suggestion',
	decorationContent = '',
	labelKey = 'label',
	groups,
	fuse: fuseProp,
	searchItems,
	content,
	pluginKey,
	editor,
} = defineProps<SuggestionMenuProps<G, T>>();

const slots = defineSlots<SuggestionMenuContentSlots<G, T> & {
	default?: (props: {
		items: T[];
		selectedIndex: number;
		selectItem: (item: T) => void;
		activateItem: (item: T) => void;
		close: () => void;
	}) => any;
}>();

const isOpen = ref(false);
const position = ref<DOMRect | null>(null);
const commandRef = ref<((item: T) => void) | null>(null);
const suggestionItems = shallowRef<T[]>([]);
const query = ref('');
const range = ref<Range | null>(null);

// match the behavior of NuxtUI CommandPalette
const items = computed(() => groups?.filter((group) => {
	if (!group.id) {
		console.warn('SuggestionMenu group is missing an `id` property');
		return false;
	}

	if (group.ignoreFilter) {
		return false;
	}

	return true;
})?.flatMap((group) => group.items?.map((item) => ({ ...item, group: group.id })) || []) || []);

const fuse = computed(() => defu({}, fuseProp, {
	fuseOptions: {
		ignoreLocation: true,
		threshold: 0.1,
		keys: [labelKey, 'suffix'],
	},
	resultLimit: 12,
	matchAllWhenSearchEmpty: true,
}));

const { results: fuseResults } = useFuse<typeof items.value[number]>(query, items, fuse);

function getGroupWithItems(group: G, items: (T & { matches?: FuseResult<T>['matches'] })[], options: { limit?: boolean } = {}) {
	if (group?.postFilter && typeof group.postFilter === 'function') {
		items = group.postFilter(query.value, items);
	}

	return {
		...group,
		items: options.limit ? items.slice(0, fuse.value.resultLimit) : items,
	};
}

function applyResultLimit(groups: ReturnType<typeof getGroupWithItems>[]) {
	let remaining = fuse.value.resultLimit;
	const limitedGroups: ReturnType<typeof getGroupWithItems>[] = [];

	for (const group of groups) {
		if (remaining <= 0) break;

		const groupItems = group.items.slice(0, remaining);

		if (groupItems.length === 0) continue;

		limitedGroups.push({
			...group,
			items: groupItems,
		});

		remaining -= groupItems.length;
	}

	return limitedGroups;
}

const filteredGroups = computed(() => {
	const filteredItems = searchItems
		? searchItems(query.value, items.value as (T & { group: string })[])
		: fuseResults.value.map((result) => ({ ...result.item, matches: result.matches }));

	const groupsById = filteredItems.reduce((acc, item) => {
		const matches = 'matches' in item ? item.matches : undefined;

		if (!item.group) {
			return acc;
		}

		acc[item.group] ||= [];
		acc[item.group]?.push({ ...item, matches });

		return acc;
	}, {} as Record<string, (T & { matches?: FuseResult<T>['matches'] })[]>);

	const fuseGroups = Object.entries(groupsById).map(([id, items]) => {
		const group = groups?.find((group) => group.id === id);
		return group ? getGroupWithItems(group, items) : undefined;
	}).filter((group) => !!group);

	const limitedFuseGroups = applyResultLimit(fuseGroups);

	const nonFuseGroups = groups
		?.map((group, index) => ({ ...group, index }))
		?.filter((group) => group.ignoreFilter && group.items?.length)
		?.map((group) => ({ ...getGroupWithItems(group, group.items || [], { limit: true }), index: group.index })) || [];

	return nonFuseGroups.reduce((acc, group) => {
		acc.splice(group.index, 0, group);
		return acc;
	}, [...limitedFuseGroups]);
});

const contentProps = toRef(() => defu(content, { align: 'start', side: 'bottom', sideOffset: 8, collisionPadding: 8 }) as PopoverContentProps);
const contentReference = {
	getBoundingClientRect: () => position.value as ClientRectObject ?? undefined,
};

const selectedIndex = ref(0);

const computedPluginKey = computed(() =>
	pluginKey instanceof PluginKey
		? pluginKey
		: new PluginKey(pluginKey || 'SuggestionPluginKey'));

/**
 * Suggestion plugin registration
 */
watch([() => editor, computedPluginKey], ([_, pluginKey], __, onCleanup) => {
	if (!editor || editor.isDestroyed) return;

	// Remove existing plugin with same key
	const existingPlugin = editor.state.plugins.find(
		(p) => p.spec.key === pluginKey,
	);

	if (existingPlugin) {
		editor.unregisterPlugin(pluginKey);
	}

	const suggestionPlugin = Suggestion<T>({
		pluginKey,
		editor,
		command: ({ editor, range: cmdRange, props: cmdProps }) => {
			if (!cmdRange) return;
			const { view, state } = editor;
			const { selection } = state;

			const isMentionCharUsed = editor.extensionManager.extensions.some(
				(ext) =>
					ext.name === 'mention'
					&& ext.options?.suggestion?.char === char,
			);

			if (!isMentionCharUsed) {
				const fromPos = (() => {
					const pos = selection.$from.pos;
					const nodeBefore = selection.$head?.nodeBefore;
					if (!nodeBefore?.text || !char) return pos;

					const text = nodeBefore.text;
					const charIndex = text.lastIndexOf(char);
					return charIndex === -1
						? pos
						: pos - text.slice(charIndex).length;
				})();

				const tr = state.tr.deleteRange(fromPos, selection.$from.pos);
				view.dispatch(tr);
			}

			const nodeAfter = view.state.selection.$to.nodeAfter;
			const hasLeadingSpace = nodeAfter?.text?.startsWith(' ');
			const adjustedRange = { ...cmdRange };
			if (hasLeadingSpace) adjustedRange.to += 1;

			cmdProps.onSelect({
				editor,
				range: adjustedRange,
				context: cmdProps.context,
			});
		},
		render: () => ({
			onStart: (renderProps) => {
				commandRef.value = renderProps.command;
				suggestionItems.value = renderProps.items;
				selectedIndex.value = nextEnabledItem(suggestionItems.value, -1);
				query.value = renderProps.query;
				range.value = renderProps.range;
				position.value = renderProps.clientRect?.() ?? null;
				isOpen.value = true;
			},
			onUpdate: (renderProps) => {
				commandRef.value = renderProps.command;
				suggestionItems.value = renderProps.items;
				query.value = renderProps.query;
				selectedIndex.value = nextEnabledItem(suggestionItems.value, -1);
				range.value = renderProps.range;
				position.value = renderProps.clientRect?.() ?? null;
			},
			onKeyDown: ({ event }) => {
				if (event.key === 'Escape') {
					closeMenu();
					return true;
				}
				else if (event.key === 'ArrowDown') {
					selectedIndex.value = nextEnabledItem(suggestionItems.value, selectedIndex.value);
					return true;
				}
				else if (event.key === 'ArrowUp') {
					selectedIndex.value = prevEnabledItem(suggestionItems.value, selectedIndex.value);
					return true;
				}
				else if ((event.key === 'Enter' || event.key === 'Tab')) {
					if (suggestionItems.value.length === 0) {
						closeMenu();
						return true;
					}
					else if (suggestionItems.value[selectedIndex.value]) {
						handleSelect(suggestionItems.value[selectedIndex.value]!);
						return true;
					}
				}

				return false;
			},
			onExit: () => {
				commandRef.value = null;
				suggestionItems.value = [];
				query.value = '';
				range.value = null;
				position.value = null;
				isOpen.value = false;
			},
		}),
		char,
		items: ({ query: query_ }) => {
			query.value = query_;
			return filteredGroups.value.flatMap((group) => group.items);
		},
		allowSpaces,
		findSuggestionMatch: (config) => {
			const match = defaultFindSuggestionMatch(config);

			if (!match) return null;
			if (!allowLeadingSpace && match.query.startsWith(' ')) return null;

			return match;
		},
		allowToIncludeChar,
		allowedPrefixes,
		startOfLine,
		decorationTag,
		decorationClass,
		decorationContent,
	});

	editor.registerPlugin(suggestionPlugin);

	onCleanup(() => {
		if (!editor?.isDestroyed) {
			editor?.unregisterPlugin(computedPluginKey.value);
		}
	});
}, { immediate: true });

function closeMenu() {
	isOpen.value = false;
}

function handleSelect(item: T) {
	closeMenu();
	commandRef.value?.(item);
}

function handleActivate(item: T) {
	const index = suggestionItems.value.indexOf(item);

	if (index !== -1) {
		selectedIndex.value = index;
	}
}

function prevEnabledItem(items: T[], start: number) {
	if (items.length <= 1) return 0;

	let index = start;

	do {
		index = (index + items.length - 1) % items.length;
	} while (index !== start && items[index]?.disabled);

	return index;
}

function nextEnabledItem(items: T[], start: number) {
	if (items.length <= 1) return 0;

	let index = start;

	do {
		index = (index + 1) % items.length;
	} while (index !== start && items[index]?.disabled);

	return index;
}
</script>

<template>
	<PopoverRoot
		v-if="editor"
		v-model:open="isOpen"
		:data-selector="selector || 'tiptap-suggestion-menu'"
		:aria-label="$t('editor.suggestions.aria_label')"
	>
		<PopoverPortal>
			<!--
				The editor keeps focus and drives the menu via keydown, so the menu must not
				steal focus. Auto-focusing the portaled content also trips a parent popover's
				focus-outside dismissal, closing it. Keep focus in the editor.
			-->
			<PopoverContent
				:reference="contentReference"
				v-bind="contentProps"
				@open-auto-focus.prevent
				@close-auto-focus.prevent
			>
				<slot
					:items="suggestionItems"
					:selected-index
					:select-item="handleSelect"
					:activate-item="handleActivate"
					:close="closeMenu"
				>
					<SuggestionMenuContent
						:groups
						:items="suggestionItems"
						:selected-index
						@select="handleSelect"
						@activate="handleActivate"
						@close="closeMenu"
					>
						<template v-for="(_, name) in omit(slots, ['default'])" #[name]="slotData" :key="name">
							<slot :name v-bind="slotData" />
						</template>
					</SuggestionMenuContent>
				</slot>
			</PopoverContent>
		</PopoverPortal>
	</PopoverRoot>
</template>
